/**
 * Single source of truth for every route in the application.
 *
 * The router, the navigation components, the sitemap generator, the prerender
 * step and the Playwright suite all read from these tables, so a route cannot
 * exist in one place and be missing from another.
 *
 * Paths here are *application-relative*. The `/clients/<slug>` prefix is
 * applied once by the router `basename` and by `withBasePath` - never inline.
 */

import type { PathwayId } from './brand.js';

export type AppRole = 'client' | 'coach' | 'client_admin' | 'system_admin';

export interface RouteDefinition {
  /** Stable identifier used by tests and analytics. */
  id: string;
  /** Application-relative path, React Router syntax. */
  path: string;
  /** Navigation and document-title label. */
  label: string;
  /** Which brand experience owns the route. */
  pathway: PathwayId;
  /** Whether the route appears in its primary navigation. */
  inNav: boolean;
  /** Roles allowed to reach the route. Empty means public. */
  roles: readonly AppRole[];
  /** Robots policy. Authenticated routes are always excluded. */
  indexable: boolean;
  /** Shown in the footer legal cluster. */
  legal?: boolean;
}

// --- Public ------------------------------------------------------------------

export const publicRoutes = {
  home: {
    id: 'home',
    path: '/',
    label: 'Home',
    pathway: 'master',
    inNav: true,
    roles: [],
    indexable: true,
  },
  personalTraining: {
    id: 'personal-training',
    path: '/personal-training',
    label: 'Personal Training',
    pathway: 'personal-training',
    inNav: true,
    roles: [],
    indexable: true,
  },
  boxing: {
    id: 'boxing',
    path: '/combat',
    label: 'Combat',
    pathway: 'boxing',
    inNav: true,
    roles: [],
    indexable: true,
  },
  programs: {
    id: 'programs',
    path: '/programs',
    label: 'Programs',
    pathway: 'master',
    inNav: true,
    roles: [],
    indexable: true,
  },
  results: {
    id: 'results',
    path: '/results',
    label: 'Results',
    pathway: 'master',
    inNav: true,
    roles: [],
    indexable: true,
  },
  about: {
    id: 'about',
    path: '/about',
    label: 'About the Coach',
    pathway: 'master',
    inNav: true,
    roles: [],
    indexable: true,
  },
  coachCard: {
    id: 'coach-card',
    path: '/about/zach.jones/id',
    label: 'Zach Jones Digital Card',
    pathway: 'master',
    inNav: false,
    roles: [],
    indexable: true,
  },
  book: {
    id: 'book',
    path: '/book',
    label: 'Book',
    pathway: 'master',
    inNav: true,
    roles: [],
    indexable: true,
  },
  contact: {
    id: 'contact',
    path: '/contact',
    label: 'Contact',
    pathway: 'master',
    inNav: false,
    roles: [],
    indexable: true,
  },

  login: {
    id: 'login',
    path: '/login',
    label: 'Client Login',
    pathway: 'master',
    inNav: true,
    roles: [],
    indexable: false,
  },
  register: {
    id: 'register',
    path: '/register',
    label: 'Create Account',
    pathway: 'master',
    inNav: false,
    roles: [],
    indexable: false,
  },
  forgotPassword: {
    id: 'forgot-password',
    path: '/forgot-password',
    label: 'Forgot Password',
    pathway: 'master',
    inNav: false,
    roles: [],
    indexable: false,
  },
  resetPassword: {
    id: 'reset-password',
    path: '/reset-password',
    label: 'Reset Password',
    pathway: 'master',
    inNav: false,
    roles: [],
    indexable: false,
  },
  verifyEmail: {
    id: 'verify-email',
    path: '/verify-email',
    label: 'Verify Email',
    pathway: 'master',
    inNav: false,
    roles: [],
    indexable: false,
  },

  privacy: {
    id: 'privacy',
    path: '/privacy',
    label: 'Privacy',
    pathway: 'master',
    inNav: false,
    roles: [],
    indexable: true,
    legal: true,
  },
  terms: {
    id: 'terms',
    path: '/terms',
    label: 'Terms & Conditions',
    pathway: 'master',
    inNav: false,
    roles: [],
    indexable: true,
    legal: true,
  },
  trainingDisclaimer: {
    id: 'training-disclaimer',
    path: '/training-disclaimer',
    label: 'Training Disclaimer',
    pathway: 'master',
    inNav: false,
    roles: [],
    indexable: true,
    legal: true,
  },
  nutritionDisclaimer: {
    id: 'nutrition-disclaimer',
    path: '/nutrition-disclaimer',
    label: 'Nutrition Disclaimer',
    pathway: 'master',
    inNav: false,
    roles: [],
    indexable: true,
    legal: true,
  },
  cancellationPolicy: {
    id: 'cancellation-policy',
    path: '/cancellation-policy',
    label: 'Cancellation Policy',
    pathway: 'master',
    inNav: false,
    roles: [],
    indexable: true,
    legal: true,
  },
  accessibility: {
    id: 'accessibility',
    path: '/accessibility',
    label: 'Accessibility',
    pathway: 'master',
    inNav: false,
    roles: [],
    indexable: false,
    legal: false,
  },
} as const satisfies Record<string, RouteDefinition>;

// --- Client portal -----------------------------------------------------------

const CLIENT_ONLY: readonly AppRole[] = ['client'];

function portalRoute(
  id: string,
  path: string,
  label: string,
  inNav = true,
): RouteDefinition {
  return {
    id: `portal-${id}`,
    path,
    label,
    pathway: 'master',
    inNav,
    roles: CLIENT_ONLY,
    indexable: false,
  };
}

export const portalRoutes = {
  root: portalRoute('root', '/portal', 'Portal', false),
  dashboard: portalRoute('dashboard', '/portal/dashboard', 'Dashboard'),
  book: portalRoute('book', '/portal/book', 'Book'),
  calendar: portalRoute('calendar', '/portal/calendar', 'Calendar'),
  sessions: portalRoute('sessions', '/portal/sessions', 'Sessions', false),
  training: portalRoute('training', '/portal/training', 'Training'),
  nutrition: portalRoute('nutrition', '/portal/nutrition', 'Nutrition'),
  workouts: portalRoute('workouts', '/portal/workouts', 'Workouts'),
  checkIns: portalRoute('check-ins', '/portal/check-ins', 'Check-Ins'),
  progress: portalRoute('progress', '/portal/progress', 'Progress'),
  analysis: portalRoute('analysis', '/portal/analysis', 'Analysis'),
  feedback: portalRoute('feedback', '/portal/feedback', 'Feedback'),
  messages: portalRoute('messages', '/portal/messages', 'Messages'),
  documents: portalRoute('documents', '/portal/documents', 'Documents'),
  forms: portalRoute('forms', '/portal/forms', 'Forms'),
  notifications: portalRoute('notifications', '/portal/notifications', 'Notifications', false),
  profile: portalRoute('profile', '/portal/profile', 'Profile'),
  settings: portalRoute('settings', '/portal/settings', 'Settings'),
  support: portalRoute('support', '/portal/support', 'Support', false),
  onboarding: portalRoute('onboarding', '/portal/onboarding', 'Onboarding', false),
  awaitingApproval: portalRoute(
    'awaiting-approval',
    '/portal/awaiting-approval',
    'Awaiting approval',
    false,
  ),
} as const satisfies Record<string, RouteDefinition>;

// --- Admin / coach portal ----------------------------------------------------

const STAFF: readonly AppRole[] = ['coach', 'client_admin', 'system_admin'];
/** One trainer operates the whole console - meal plans, programs, check-ins, settings. */
const ADMIN_ONLY: readonly AppRole[] = STAFF;

function adminRoute(
  id: string,
  path: string,
  label: string,
  roles: readonly AppRole[] = STAFF,
  inNav = true,
): RouteDefinition {
  return {
    id: `admin-${id}`,
    path,
    label,
    pathway: 'master',
    inNav,
    roles,
    indexable: false,
  };
}

export const adminRoutes = {
  root: adminRoute('root', '/admin', 'Admin', STAFF, false),
  dashboard: adminRoute('dashboard', '/admin/dashboard', 'Dashboard'),
  calendar: adminRoute('calendar', '/admin/calendar', 'Calendar'),
  bookings: adminRoute('bookings', '/admin/bookings', 'Booking Requests'),
  clients: adminRoute('clients', '/admin/clients', 'Clients'),
  client: adminRoute('client', '/admin/clients/:clientId', 'Client', STAFF, false),
  trainingPlans: adminRoute('plans-training', '/admin/plans/training', 'Training Plans'),
  exercises: adminRoute('exercises', '/admin/exercises', 'Exercise Library'),
  nutritionPlans: adminRoute('plans-nutrition', '/admin/plans/nutrition', 'Nutrition Plans'),
  checkIns: adminRoute('check-ins', '/admin/check-ins', 'Check-Ins'),
  forms: adminRoute('forms', '/admin/forms', 'Forms'),
  feedback: adminRoute('feedback', '/admin/feedback', 'Feedback'),
  availability: adminRoute('availability', '/admin/availability', 'Availability'),
  sessionTypes: adminRoute('session-types', '/admin/session-types', 'Session Types', ADMIN_ONLY),
  packages: adminRoute('packages', '/admin/packages', 'Packages', ADMIN_ONLY),
  messages: adminRoute('messages', '/admin/messages', 'Messages'),
  documents: adminRoute('documents', '/admin/documents', 'Documents'),
  content: adminRoute('content', '/admin/content', 'Public Content', ADMIN_ONLY),
  media: adminRoute('media', '/admin/media', 'Media', ADMIN_ONLY),
  results: adminRoute('results', '/admin/results', 'Results & Testimonials', ADMIN_ONLY),
  reports: adminRoute('reports', '/admin/reports', 'Reports', ADMIN_ONLY),
  notifications: adminRoute('notifications', '/admin/notifications', 'Notifications'),
  settings: adminRoute('settings', '/admin/settings', 'Settings', ADMIN_ONLY),
  audit: adminRoute('audit', '/admin/audit', 'Audit Log', ADMIN_ONLY),
} as const satisfies Record<string, RouteDefinition>;

// --- Derived views -----------------------------------------------------------

export const allRoutes: readonly RouteDefinition[] = [
  ...Object.values(publicRoutes),
  ...Object.values(portalRoutes),
  ...Object.values(adminRoutes),
];

/** Header navigation, in the order required by the brief. */
export const primaryNav: readonly RouteDefinition[] = [
  publicRoutes.home,
  publicRoutes.personalTraining,
  publicRoutes.boxing,
  publicRoutes.programs,
  publicRoutes.results,
  publicRoutes.about,
  publicRoutes.book,
  publicRoutes.login,
];

// Widened to `RouteDefinition` before filtering: the object literal preserves each
// route's exact shape, and `legal` is only present on the routes that set it, so the
// union of those literals has no common `legal` property to read.
export const legalNav: readonly RouteDefinition[] = (
  Object.values(publicRoutes) as readonly RouteDefinition[]
).filter((route) => route.legal === true);

export const portalNav: readonly RouteDefinition[] = Object.values(portalRoutes).filter(
  (route) => route.inNav,
);

export const adminNav: readonly RouteDefinition[] = Object.values(adminRoutes).filter(
  (route) => route.inNav,
);

/** Static, crawlable routes for the sitemap and the prerender step. */
export const indexableRoutes: readonly RouteDefinition[] = Object.values(publicRoutes).filter(
  (route) => route.indexable && !route.path.includes(':'),
);

/** Landing route for a role after authentication. */
export function landingPathForRole(role: AppRole): string {
  return role === 'client' ? portalRoutes.dashboard.path : adminRoutes.dashboard.path;
}

/**
 * Which brand experience owns a given pathname.
 *
 * Drives the header wordmark, accent colour and the pathway preselection on the
 * consultation form. Resolved from the route table rather than from a switch
 * statement in a component, so adding a pathway route cannot leave the header
 * showing the wrong brand.
 */
export function pathwayForPath(pathname: string): PathwayId {
  // Strip the deployment base path if a caller passes a full location pathname.
  const normalised = pathname.replace(/\/+$/, '') || '/';

  const match = Object.values(publicRoutes).find(
    (route) => route.path !== '/' && normalised.endsWith(route.path),
  );

  return match?.pathway ?? 'master';
}

/** Builds a concrete path from a parameterised route definition. */
export function buildPath(
  route: RouteDefinition,
  params: Readonly<Record<string, string>> = {},
): string {
  return route.path.replace(/:([A-Za-z0-9_]+)/g, (_match, key: string) => {
    const value = params[key];
    if (value === undefined) {
      throw new Error(`Missing route parameter "${key}" for route "${route.id}".`);
    }
    return encodeURIComponent(value);
  });
}
